import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";

const TEAMS = [
  "Worship", "Ushering", "Media & Production", "Children",
  "Youth", "Intercession", "Outreach / Evangelism", "Hospitality",
];

export default function JoinTeam() {
  const [f, setF] = useState({
    full_name: "", email: "", phone: "",
    team_interest: "", skills: "", availability: "", why_join: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.team_interest) return toast.error("Please choose a team");
    setLoading(true);
    try {
      await api.post("/join-team", f);
      setDone(true);
      toast.success("Thank you! We'll be in touch soon.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-3xl mx-auto px-6 lg:px-10 py-32 text-center" data-testid="join-team-success">
        <p className="overline text-terracotta mb-4">Welcome Aboard</p>
        <h1 className="font-heading font-black text-5xl tracking-tighter text-olive">See you soon.</h1>
        <p className="mt-6 text-ink/75 text-lg">
          Your application to join <span className="font-semibold">{f.team_interest}</span> was received.
          A team leader will reach out shortly.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="join-team-page">
      <section className="bg-cream border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <p className="overline text-terracotta mb-4">Serve</p>
          <h1 className="font-heading font-black text-5xl sm:text-6xl tracking-tighter text-olive leading-[0.95] max-w-3xl">
            Join a <em className="italic font-light text-terracotta">team.</em>
          </h1>
          <p className="mt-6 text-ink/70 text-lg max-w-2xl">
            God has placed a gift in you. Let's build together. Fill in the form below and a team
            leader will reach out.
          </p>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-6 lg:px-10 py-16">
        <form onSubmit={submit} className="space-y-6 bg-white border border-border p-8 md:p-12">
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <Label htmlFor="full_name">Full name *</Label>
              <Input id="full_name" required value={f.full_name} onChange={set("full_name")} data-testid="jt-name" className="mt-2 h-11" />
            </div>
            <div>
              <Label htmlFor="email">Email *</Label>
              <Input id="email" type="email" required value={f.email} onChange={set("email")} data-testid="jt-email" className="mt-2 h-11" />
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <Label htmlFor="phone">Phone *</Label>
              <Input id="phone" required value={f.phone} onChange={set("phone")} data-testid="jt-phone" className="mt-2 h-11" />
            </div>
            <div>
              <Label>Team interest *</Label>
              <Select value={f.team_interest} onValueChange={set("team_interest")}>
                <SelectTrigger data-testid="jt-team" className="mt-2 h-11">
                  <SelectValue placeholder="Select a team" />
                </SelectTrigger>
                <SelectContent>
                  {TEAMS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label htmlFor="skills">Skills / Gifting</Label>
            <Input id="skills" value={f.skills} onChange={set("skills")} data-testid="jt-skills" className="mt-2 h-11" />
          </div>
          <div>
            <Label htmlFor="availability">Availability</Label>
            <Input id="availability" placeholder="e.g. Sundays + Wednesday evenings" value={f.availability} onChange={set("availability")} data-testid="jt-availability" className="mt-2 h-11" />
          </div>
          <div>
            <Label htmlFor="why_join">Why do you want to serve?</Label>
            <Textarea id="why_join" rows={4} value={f.why_join} onChange={set("why_join")} data-testid="jt-why" className="mt-2" />
          </div>
          <Button
            type="submit"
            disabled={loading}
            data-testid="join-team-submit"
            className="bg-terracotta hover:bg-terracotta-hover text-white rounded-none h-12 px-10 text-base font-semibold"
          >
            {loading ? "Submitting…" : "Submit Application"}
          </Button>
        </form>
      </section>
    </div>
  );
}
